import type { HTMLAttributes, ReactNode } from 'react';

import styles from './Card.module.css';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardElevation = 'flat' | 'raised';

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title' | 'className'> {
  title?: ReactNode | undefined;
  description?: ReactNode | undefined;
  /** Rendered on the header's trailing edge — filters, a menu, a primary action. */
  actions?: ReactNode | undefined;
  footer?: ReactNode | undefined;
  padding?: CardPadding | undefined;
  elevation?: CardElevation | undefined;
  as?: 'section' | 'article' | 'div' | undefined;
  className?: string | undefined;
  children?: ReactNode | undefined;
}

export function Card({
  title,
  description,
  actions,
  footer,
  padding = 'md',
  elevation = 'flat',
  as: Element = 'section',
  className,
  children,
  ...rest
}: CardProps) {
  const hasHeader = title !== undefined || description !== undefined || actions !== undefined;

  return (
    <Element
      {...rest}
      className={[styles.card, styles[elevation], styles[`padding-${padding}`], className]
        .filter(Boolean)
        .join(' ')}
    >
      {hasHeader ? (
        <header className={styles.header}>
          <div>
            {title !== undefined ? <h2 className={styles.title}>{title}</h2> : null}
            {description !== undefined ? <p className={styles.description}>{description}</p> : null}
          </div>
          {actions !== undefined ? <div>{actions}</div> : null}
        </header>
      ) : null}

      {children}

      {footer !== undefined ? <footer className={styles.footer}>{footer}</footer> : null}
    </Element>
  );
}
